(ns manage.adset-decision-tree-page.tree-generation-ocp.hook
  (:require ["@tanstack/react-query" :as query]
            ["react-hook-form" :as rhf]
            [cljs-bean.core :refer [->clj]]
            [clojure.string :as str]
            [common.form :as form]
            [common.queries :refer [use-mutation use-query]]
            [common.resource.query.adset :as adset-queries]
            [common.resource.query.decision-tree :as dt-queries]
            [manage.ad-variant.creation.feed.mapping.util :as mapping.util]
            [manage.adset-decision-tree-page.event :as event]
            [manage.adset-decision-tree-page.node-library :as node-library]
            [manage.adset-decision-tree-page.property :as property]
            [manage.adset-decision-tree-page.sub :as sub]
            [manage.adset-decision-tree-page.view.context :as context]
            [manage.adset-decision-tree-page.view.predicate-editor.converter
             :as predicate-converter]
            [manage.adset-decision-tree-page.view.side-panel.property-form
             :refer [use-property-form use-property-resolver]]
            [manage.content-variant-page.queries :as content-queries]
            [mate.core :as mc]
            [re-frame.core :as rf]
            [uix.core :as uix :refer [defhook]]
            [uix.re-frame :refer [use-subscribe]]))

(defn get-base-node-data [node-type-str]
  (when (some? node-type-str)
    (get node-library/type->node
         (keyword node-library/node-type-ns node-type-str))))

(def supported-properties
  #{:meta.facebook/adset-id :targeting/predicate :feed/properties})

(defn get-mapping-property
  [{:keys [decision-node-type type]}]
  (some
   (fn [{:keys [properties category]}]
     (some
      (fn [{:keys [id disabled] :as property}]
        (when (and (contains? supported-properties id) (not disabled))
          (-> property property/resolve-property (assoc :category category))))
      properties))
   [(get node-library/type->node type) (get node-library/type->node decision-node-type)]))

(def get-mapping-property-id (comp :id get-mapping-property))

(defn decision-node->option
  [{:keys [outcome-node-type icon type category]}]
  (when (and
         (some? (get-mapping-property-id {:decision-node-type type :type outcome-node-type}))
         (= category :decision-node))
    {:decision-node-type type
     :value outcome-node-type
     :label (get-in node-library/type->node [type :name])
     :icon icon}))

(defhook use-node-type-options []
  (let [{:keys [available-nodes]} (uix/use-context context/decision-trees-context)]
    (uix/use-memo #(clj->js (keep decision-node->option available-nodes)) [available-nodes])))

(defhook use-strategy-columns []
  (let [{:keys [advertiser-id adset-id]} (use-subscribe [:url-context])
        {{feed-id :feedId} :data} (use-query
                                   (adset-queries/adset-query {:adset-id adset-id}))
        {{{:keys [field-classes]} :data} :data :keys [isSuccess]}
        (use-query
         (content-queries/get-content-feed advertiser-id feed-id))]
    (uix/use-memo #(when isSuccess
                     (keep (fn [{:keys [class] column-name :name}]
                             (when (= class mapping.util/strategy-class)
                               column-name))
                           field-classes))
                  [field-classes isSuccess])))

(defhook use-classification-form-state [default-value]
  (form/use-form #js {:mode "all"
                      :defaultValues default-value}))

(defn get-classified-columns [column-classes]
  (->> column-classes
       (keep (fn [[column-name {:keys [value] :as column-data}]]
               (when (some? value)
                 (assoc column-data :column-name (name column-name)))))
       (sort-by :column-name)))

(defhook use-classified-columns [{:keys [control]}]
  (let [column-classes (->clj (rhf/useWatch #js {:control control}))]
    (uix/use-memo #(get-classified-columns column-classes) [column-classes])))

(defn get-column-values [strategy-columns results force-up-to-date?]
  (let [results (->clj results)]
    (when (and (every? :isSuccess results)
               (or (not force-up-to-date?) (not-any? :isStale results)))
      (mapcat (fn [column-name {{column-values :data} :data}]
                (map-indexed (fn [i column-value]
                               {:value-alias (str "value-" i)
                                :column-value column-value
                                :column-name column-name})
                             column-values))
              strategy-columns
              results))))

(defhook use-column-values [{:keys [force-up-to-date]}]
  (let [strategy-columns (use-strategy-columns)
        {:keys [advertiser-id adset-id]} (use-subscribe [:url-context])
        {{:keys [feedId]} :data} (use-query
                                  (adset-queries/adset-query {:adset-id adset-id}))
        get-column-values* (uix/use-callback
                            #(get-column-values strategy-columns % force-up-to-date)
                            [strategy-columns force-up-to-date])]
    (query/useQueries
     #js {:queries (to-array
                    (map #(content-queries/content-build-unique-values-query
                           {:advertiser-id advertiser-id
                            :feed-importer-id feedId
                            :column-name %})
                         strategy-columns))
          :combine get-column-values*})))

(defhook use-column-name->value-details []
  (let [column-values (use-column-values {})]
    (uix/use-memo #(group-by :column-name column-values) [column-values])))

(defhook use-column-name->properties [classified-columns]
  (let [column-name->value-details (use-column-name->value-details)]
    (uix/use-memo
     #(mc/index-by
       :column-name
       (fn [{:keys [value column-name]}]
         (let [base-node-data (get-base-node-data value)
               {:keys [category] :as mapping-property} (get-mapping-property base-node-data)]
           (case category
             :decision-node [(assoc mapping-property
                                    :data-path [(keyword column-name)]
                                    :category category)]
             :outcome-node (->> (get column-name->value-details column-name)
                                (sort :value)
                                (map (fn [{:keys [column-value value-alias]}]
                                       (assoc mapping-property
                                              :data-path [(keyword column-name)
                                                          (keyword value-alias)]
                                              :column-value column-value)))))))
       classified-columns)
     [classified-columns column-name->value-details])))

(defhook use-value-mapping-properties [classified-columns]
  (let [column-name->properties (use-column-name->properties classified-columns)]
    (uix/use-memo #(mapcat val column-name->properties)
                  [column-name->properties])))

(defhook use-value-mapping-form-control
  [classification-form-control be-default-value]
  (let [classified-columns (use-classified-columns classification-form-control)
        properties (use-value-mapping-properties classified-columns)
        {:keys [reset] :as form-control}
        (use-property-form {:initial-form-value be-default-value
                            :properties properties})

        reset-on-column-type-change
        (uix/use-callback (fn [_data {changed-column-name :name}]
                            (reset (fn [current-form-state]
                                     (js/Object.assign current-form-state
                                                       (clj->js {changed-column-name nil})))))
                          [reset])]
    (form/use-form-callback classification-form-control reset-on-column-type-change {})
    form-control))

(defhook use-be-classification-value [mappings]
  (let [strategy-columns (set (use-strategy-columns))]
    (uix/use-memo
     #(->> mappings
           (keep
            (fn [{:keys [targeting-column node-type]}]
              (when (and (some? node-type) (contains? strategy-columns targeting-column))
                [targeting-column (-> node-type get-base-node-data decision-node->option)])))
           (into {})
           (clj->js))
     [mappings strategy-columns])))

(defn be->column-value-mapping
  [{:keys [labels targeting-column] :as be-mapping}
   [{:keys [category] :as first-property} :as column-properties]
   column-path->value-alias]
  (let [column-value->properties (mc/index-by :column-value column-properties)]
    (case category
      :decision-node (get-in be-mapping (:strategy-config-path first-property))
      :outcome-node
      (->> labels
           (map (fn [{:keys [label] :as be-label-mapping}]
                  (let [{:keys [strategy-config-path]} (get column-value->properties label)]
                    [(keyword (get column-path->value-alias [targeting-column label]))
                     (get-in be-label-mapping strategy-config-path)])))
           (into {}))
      nil)))

(defhook use-be-value-mapping-value [mappings classified-columns]
  (let [column-name->properties (use-column-name->properties classified-columns)
        {:keys [properties-context]} (uix/use-context context/decision-trees-context)
        column-values (use-column-values {:force-up-to-date true})]
    (uix/use-memo
     #(when (and column-values (seq classified-columns))
        (let [column-path->value-alias (mc/index-by (juxt :column-name :column-value)
                                                    :value-alias
                                                    column-values)]
          (->> mappings
               (map (fn [{:keys [targeting-column] :as be-mapping}]
                      [(keyword targeting-column)
                       (be->column-value-mapping
                        be-mapping
                        (get column-name->properties targeting-column)
                        column-path->value-alias)]))
               (into {})
               (property/->form (mapcat val column-name->properties) properties-context)
               (clj->js))))
     [mappings column-values column-name->properties properties-context classified-columns])))

(defhook use-be-default-value []
  (let [url-context (use-subscribe [:url-context])
        {{:keys [mappings]} :data
         :keys [isLoading]} (use-query (dt-queries/targeting-config-query url-context))
        column-values (use-column-values {})
        be-classification (use-be-classification-value mappings)
        be-value-mapping (use-be-value-mapping-value mappings (get-classified-columns
                                                               (->clj be-classification)))]
    (when (and (not isLoading) column-values)
      {:be-classification be-classification
       :be-value-mapping be-value-mapping
       :mappings mappings})))

(defhook use-start-save-targeting-config-flow [existing-mappings]
  (let [editor-tree-id (use-subscribe [::sub/decision-tree-id])
        url-context (use-subscribe [:url-context])
        initial-tree-id (uix/use-ref editor-tree-id)
        {save-targeting-config :mutate} (use-mutation
                                         (dt-queries/targeting-config-mutation url-context))]
    ;; When the generation was successful the editor's currently loaded draft-id will change
    ;; to the generated one.
    (uix/use-effect
     #(when (and (some? editor-tree-id) (not= editor-tree-id @initial-tree-id))
        (reset! initial-tree-id nil)
        (rf/dispatch [::event/toggle-auto-layout true])
        (rf/dispatch [:ocp/close]))
     [editor-tree-id])
    (uix/use-callback #(save-targeting-config {:targeting-config %
                                               :is-update (some? existing-mappings)
                                               :draft-id editor-tree-id})
                      [save-targeting-config existing-mappings editor-tree-id])))

(defn arrange-mapping-value [mapping-value {:keys [strategy-config-path]}]
  (when mapping-value (assoc-in {} strategy-config-path mapping-value)))

(defn get-be-column-targeting-config
  [{:keys [column-name column-value-mapping path->column-value decision-node-type]
    [{:keys [category] :as first-property} :as column-properties] :column-properties}]
  (merge {:targeting-column column-name
          :mapping "mapped"
          :node-type (name decision-node-type)}
         (case category
           :decision-node (arrange-mapping-value column-value-mapping first-property)
           :outcome-node
           {:labels
            (mapv (fn [{[_ value-alias] :data-path :as property}]
                    (merge {:label (get path->column-value [column-name (name value-alias)])}
                           (arrange-mapping-value (get column-value-mapping value-alias)
                                                  property)))
                  column-properties)})))

(defn get-be-targeting-config
  [{:keys [value-mapping classified-columns column-values column-name->properties
           properties-context]}]
  (let [column-name->node-type (mc/index-by :column-name :value classified-columns)
        path->column-value (mc/index-by (juxt :column-name :value-alias) :column-value
                                        column-values)
        clj-value-mapping (->clj value-mapping)]
    (keep
     (fn [[column-name column-value-mapping]]
       (when-let [{:keys [decision-node-type]}
                  (get-base-node-data (get column-name->node-type (name column-name)))]
         (get-be-column-targeting-config
          {:path->column-value path->column-value
           :column-name (name column-name)
           :column-properties (get column-name->properties (name column-name))
           :column-value-mapping column-value-mapping
           :decision-node-type decision-node-type})))
     (property/<-form (mapcat val column-name->properties)
                      properties-context
                      clj-value-mapping))))

(defhook use-save-targeting-config
  [classified-columns {get-value-mapping :getValues :keys [trigger]} set-selected-column mappings]
  (let [start-save-flow (use-start-save-targeting-config-flow mappings)
        column-values (use-column-values {})
        column-name->properties (use-column-name->properties classified-columns)
        {:keys [properties-context]} (uix/use-context context/decision-trees-context)
        property-resolver (use-property-resolver (->> column-name->properties
                                                      (mapcat val)
                                                      (filter :strategy-required)))]
    (uix/use-callback
     #(let [value-mapping (get-value-mapping)]
        (if-let [errors (-> value-mapping property-resolver ->clj :errors seq)]
          ;; Show errors and go the first colum with error
          (do (trigger)
              (set-selected-column (some (fn [{:keys [column-name] :as column}]
                                           (when (= (name (key (first errors))) column-name)
                                             column))
                                         classified-columns))
              (rf/dispatch [:modal/open {:id :tree-generation-ocp/save-error-modal}]))
          (start-save-flow (get-be-targeting-config
                            {:value-mapping value-mapping
                             :classified-columns classified-columns
                             :column-values column-values
                             :column-name->properties column-name->properties
                             :properties-context properties-context}))))
     [get-value-mapping column-values column-name->properties properties-context
      set-selected-column property-resolver classified-columns start-save-flow trigger])))

(defhook use-predicate-summary [{:keys [property mapping-value]}]
  (let [{:keys [output]} (context/use-property-context property)
        predicate-summaries (predicate-converter/get-predicate-summaries
                             output
                             mapping-value)]
    (when (seq predicate-summaries)
      (str/join "; " predicate-summaries))))

(defhook use-expanded-state []
  (let [[collapsed-columns set-collapsed-columns] (uix/use-state #{})]
    {:set-column-expansion (uix/use-callback (fn [column expanded]
                                               (set-collapsed-columns
                                                #((if expanded disj conj) % column)))
                                             [])
     :is-column-expanded (uix/use-callback #(not (contains? collapsed-columns %))
                                           [collapsed-columns])}))
